import { checkPdfProtection, isPdfSignable } from './pdfVerification';
import { TFileInfo } from '../types/appTypes';

/**
 * Validation result for a PDF file
 */
export interface PdfValidationResult {
  /** Whether the PDF passed validation */
  isValid: boolean;
  /** Translation key for error message if validation failed */
  errorKey?: string;
  /** Additional error details for logging */
  errorDetails?: string;
  /** File that was validated */
  file: TFileInfo;
}

/**
 * Validate a single PDF file for signing compatibility
 *
 * @param file - The file info object containing the PDF data
 * @returns Promise with validation result
 */
export async function validatePdfForSigning(file: TFileInfo): Promise<PdfValidationResult> {
  console.log(`[PDF Validation] Starting validation for: ${file.filename}`);

  try {
    // Check if content is ArrayBuffer
    if (!(file.content instanceof ArrayBuffer)) {
      console.error(`[PDF Validation] ${file.filename}: Content is not ArrayBuffer`);
      return {
        isValid: false,
        errorKey: 'documentList:pdfVerification.invalidFile',
        errorDetails: 'File content is not an ArrayBuffer',
        file,
      };
    }

    console.log(`[PDF Validation] ${file.filename}: ArrayBuffer size before validation: ${file.content.byteLength} bytes`);

    // IMPORTANT: Clone the ArrayBuffer before passing to PDF.js
    // PDF.js may transfer/detach the ArrayBuffer when passing it to the worker,
    // which would make it unusable for the subsequent upload
    const contentClone = file.content.slice(0);
    console.log(`[PDF Validation] ${file.filename}: Created ArrayBuffer clone for validation`);

    console.log(`[PDF Validation] ${file.filename}: Calling checkPdfProtection...`);

    // Verify PDF protection and permissions using the CLONE
    const pdfInfo = await checkPdfProtection(contentClone);

    console.log(`[PDF Validation] ${file.filename}: ArrayBuffer size after validation: ${file.content.byteLength} bytes (should be unchanged)`);

    console.log(`[PDF Validation] ${file.filename}: checkPdfProtection succeeded`, {
      isEncrypted: pdfInfo.isEncrypted,
      isPasswordProtected: pdfInfo.isPasswordProtected,
      hasPermissions: !!pdfInfo.permissions,
      numPages: pdfInfo.numPages
    });

    // Check if PDF is signable
    const signabilityCheck = isPdfSignable(pdfInfo);

    if (!signabilityCheck.isSignable) {
      console.warn(`[PDF Validation] ${file.filename}: PDF is not signable - ${signabilityCheck.reason}`);
      return {
        isValid: false,
        errorKey: `documentList:${signabilityCheck.reason}`,
        errorDetails: `Reason: ${signabilityCheck.reason}`,
        file,
      };
    }

    // PDF is valid and signable
    console.log(`[PDF Validation] ${file.filename}: ✅ VALID and signable`);
    return {
      isValid: true,
      file,
    };
  } catch (error) {
    console.error(`[PDF Validation] ${file.filename}: ERROR caught:`, error);
    console.error(`[PDF Validation] ${file.filename}: Error type:`, error?.constructor?.name);
    console.error(`[PDF Validation] ${file.filename}: Error message:`, error instanceof Error ? error.message : String(error));

    // Check if error is due to worker loading failure (CDN blocked, network issue, etc.)
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isWorkerError =
      errorMessage.includes('worker') ||
      errorMessage.includes('Loading') ||
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('CDN');

    console.error(`[PDF Validation] ${file.filename}: isWorkerError check:`, isWorkerError);
    console.error(`[PDF Validation] ${file.filename}: Error message keywords:`, {
      hasWorker: errorMessage.includes('worker'),
      hasLoading: errorMessage.includes('Loading'),
      hasNetwork: errorMessage.includes('network'),
      hasFetch: errorMessage.includes('fetch'),
      hasCDN: errorMessage.includes('CDN')
    });

    if (isWorkerError) {
      console.error(
        `[PDF Validation] ${file.filename}: ❌ REJECTING - Worker error detected. File will be rejected for security.`
      );
      // CHANGED: Reject file when worker fails for security - don't allow unverified PDFs
      return {
        isValid: false,
        errorKey: 'documentList:pdfVerification.workerFailed',
        errorDetails: `Worker loading failed: ${errorMessage}`,
        file,
      };
    }

    // For other errors, reject the file
    console.error(`[PDF Validation] ${file.filename}: ❌ REJECTING - Other error`);
    return {
      isValid: false,
      errorKey: 'documentList:pdfVerification.failed',
      errorDetails: errorMessage,
      file,
    };
  }
}

/**
 * Validate multiple PDF files for signing compatibility
 *
 * @param files - Array of file info objects to validate
 * @returns Promise with array of validation results
 */
export async function validatePdfsForSigning(
  files: TFileInfo[]
): Promise<PdfValidationResult[]> {
  // Validate all files in parallel
  const validationPromises = files.map((file) => validatePdfForSigning(file));
  return Promise.all(validationPromises);
}

/**
 * Filter out invalid PDFs and return only valid ones with their validation results
 *
 * @param files - Array of file info objects to validate and filter
 * @returns Promise with object containing valid files and all results
 */
export async function filterValidPdfs(files: TFileInfo[]): Promise<{
  validFiles: TFileInfo[];
  invalidFiles: TFileInfo[];
  results: PdfValidationResult[];
}> {
  const results = await validatePdfsForSigning(files);

  const validFiles = results.filter((r) => r.isValid).map((r) => r.file);
  const invalidFiles = results.filter((r) => !r.isValid).map((r) => r.file);

  return {
    validFiles,
    invalidFiles,
    results,
  };
}
